/**
 * API client with automatic authentication handling, token refresh, and retry logic
 */

import { isDemoMode } from '@/demo/config'
import { demoFetch } from '@/demo/router'

// In demo mode every request is answered locally by the demo router.
const doFetch: typeof fetch = (input, init) =>
  isDemoMode ? demoFetch(String(input), init) : fetch(input, init)

class ApiError extends Error {
  status: number
  data?: any

  constructor(status: number, message: string, data?: any) {
    super(message)
    this.name = 'ApiError'
    this.status = status
    this.data = data
  }
}

interface ApiClientOptions {
  baseURL?: string
  retries?: number
  retryDelay?: number
}

// Flag to prevent multiple refresh attempts
let isRefreshing = false
let refreshPromise: Promise<boolean> | null = null

class ApiClient {
  private baseURL: string

  constructor(options: ApiClientOptions = {}) {
    // Use environment variable if available, otherwise use proxy path
    this.baseURL = options.baseURL || import.meta.env.VITE_API_URL || 'http://localhost:8000'
    this.baseURL = this.baseURL + '/api/v1'
  }

  /**
   * Attempt to refresh the access token using the refresh token
   */
  private async attemptTokenRefresh(): Promise<boolean> {
    const refreshToken = localStorage.getItem('refresh_token')
    if (!refreshToken) {
      return false
    }

    // If already refreshing, wait for the existing refresh to complete
    if (isRefreshing && refreshPromise) {
      return refreshPromise
    }

    isRefreshing = true
    refreshPromise = this.doTokenRefresh(refreshToken)

    try {
      return await refreshPromise
    } finally {
      isRefreshing = false
      refreshPromise = null
    }
  }

  private async doTokenRefresh(refreshToken: string): Promise<boolean> {
    try {
      const response = await doFetch(`${this.baseURL}/auth/refresh`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ refresh_token: refreshToken })
      })

      if (!response.ok) {
        console.warn('Token refresh failed:', response.status)
        return false
      }

      const data = await response.json()
      localStorage.setItem('access_token', data.access_token)

      // Note: We don't update session_expiry here because it tracks the refresh token
      // expiry, not the access token expiry. The refresh token itself hasn't changed.

      return true
    } catch (error) {
      console.error('Token refresh error:', error)
      return false
    }
  }

  /**
   * Clear all session data
   */
  private clearSession(): void {
    localStorage.removeItem('access_token')
    localStorage.removeItem('refresh_token')
    localStorage.removeItem('user_data')
    localStorage.removeItem('session_expiry')
    localStorage.removeItem('remember_me')
    sessionStorage.removeItem('session_expiry')
  }

  private async makeRequest<T>(
    url: string,
    options: RequestInit = {},
    retryCount = 0
  ): Promise<T> {
    const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`

    // Add authorization header if token exists
    const token = localStorage.getItem('access_token')
    const headers = new Headers(options.headers)

    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    // Ensure Content-Type is set for JSON requests (but not for FormData)
    if (!headers.has('Content-Type') && options.body && typeof options.body === 'string') {
      headers.set('Content-Type', 'application/json')
    }

    const response = await doFetch(fullUrl, {
      ...options,
      headers
    })

    const isAuthEndpoint = /\/auth\/(login|register|forgot-password|reset-password|refresh)/.test(new URL(fullUrl).pathname)
    const hasToken = Boolean(token)

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ detail: 'Unknown error' }))
      // Handle both string detail and object detail (FastAPI returns object for structured errors)
      const detail = errorData?.detail
      const message =
        (typeof detail === 'string' ? detail : detail?.message) ||
        errorData?.message ||
        `HTTP ${response.status}: ${response.statusText}`

      // Handle 401 errors with token refresh
      if (response.status === 401 && hasToken && !isAuthEndpoint) {
        // Try to refresh the token (only once)
        if (retryCount === 0) {
          const refreshed = await this.attemptTokenRefresh()
          if (refreshed) {
            // Retry the original request with the new token
            return this.makeRequest<T>(url, options, 1)
          }
        }

        // Refresh failed or retry failed - clear session and redirect
        console.warn('Authentication failed after refresh attempt, redirecting to login')
        this.clearSession()
        window.location.replace('/login')
      }

      // Handle 403 errors - clear session and redirect
      if (response.status === 403 && hasToken && !isAuthEndpoint) {
        console.warn('Authorization failed, clearing session and redirecting to login')
        this.clearSession()
        window.location.replace('/login')
      }

      throw new ApiError(response.status, message, errorData)
    }

    if (response.status === 204) {
      return undefined as T
    }

    const contentType = response.headers.get('content-type') || ''
    if (!contentType.includes('application/json')) {
      return undefined as T
    }

    return response.json()
  }

  async get<T>(url: string, options: RequestInit = {}): Promise<T> {
    return this.makeRequest<T>(url, { ...options, method: 'GET' })
  }

  async post<T>(url: string, data?: any, options: RequestInit = {}): Promise<T> {
    // Handle FormData differently - don't stringify it
    const isFormData = data instanceof FormData
    return this.makeRequest<T>(url, {
      ...options,
      method: 'POST',
      body: data ? (isFormData ? data : JSON.stringify(data)) : undefined
    })
  }

  async put<T>(url: string, data?: any, options: RequestInit = {}): Promise<T> {
    // Handle FormData differently - don't stringify it
    const isFormData = data instanceof FormData
    return this.makeRequest<T>(url, {
      ...options,
      method: 'PUT',
      body: data ? (isFormData ? data : JSON.stringify(data)) : undefined
    })
  }

  async patch<T>(url: string, data?: any, options: RequestInit = {}): Promise<T> {
    // Handle FormData differently - don't stringify it
    const isFormData = data instanceof FormData
    return this.makeRequest<T>(url, {
      ...options,
      method: 'PATCH',
      body: data ? (isFormData ? data : JSON.stringify(data)) : undefined
    })
  }

  async delete<T>(url: string, options: RequestInit = {}): Promise<T> {
    return this.makeRequest<T>(url, { ...options, method: 'DELETE' })
  }

  async fetch(url: string, options: RequestInit = {}, retryCount = 0): Promise<Response> {
    const fullUrl = url.startsWith('http') ? url : `${this.baseURL}${url}`

    const token = localStorage.getItem('access_token')
    const headers = new Headers(options.headers)

    if (token && !headers.has('Authorization')) {
      headers.set('Authorization', `Bearer ${token}`)
    }

    // Ensure Content-Type is set for JSON requests (but not for FormData)
    if (!headers.has('Content-Type') && options.body && typeof options.body === 'string') {
      headers.set('Content-Type', 'application/json')
    }

    const response = await doFetch(fullUrl, {
      ...options,
      headers
    })

    const hasToken = Boolean(token)

    // Handle 401 errors with token refresh
    if (response.status === 401 && hasToken) {
      if (retryCount === 0) {
        const refreshed = await this.attemptTokenRefresh()
        if (refreshed) {
          return this.fetch(url, options, 1)
        }
      }

      console.warn('Authentication failed after refresh attempt, redirecting to login')
      this.clearSession()
      window.location.replace('/login')
      throw new Error('Session expired. Redirecting to login...')
    }

    // Handle 403 errors
    if (response.status === 403 && hasToken) {
      console.warn('Authorization failed, clearing session and redirecting to login')
      this.clearSession()
      window.location.replace('/login')
      throw new Error('Session expired. Redirecting to login...')
    }

    return response
  }
}

// Export a default instance
export const apiClient = new ApiClient()

export { ApiError }

// Export the class for custom instances
export default ApiClient

// ===========================
// RECONCILIATION API METHODS
// ===========================

export interface ReconciliationFilters {
  match_status?: 'matched' | 'unmatched' | 'committed' | 'all'
  client_ids?: string[]
  search?: string
  date_from?: string
  date_to?: string
  skip?: number
  limit?: number
}

// New types for matched pairs with preview
export interface MatchedTransactionData {
  id: string
  reference: string
  from: string
  to: string
  date: string
  amount: string
  purpose: string
  matched_invoice: string
}

export interface MatchedInvoiceData {
  id: string
  filename: string
  invoice_date: string
  invoice_number: string
  mirage_invoice_number: string
  mirage_counterparty_id?: number | null
  vendor: string
  customer: string
  amount: string
  type: string
  matched_transaction: string
}

export interface JournalLine {
  account: string
  description: string
  amount: string
  // APX fields for analytics-enabled accounts (LevelAcc=2)
  apx_id?: number
  client_code?: string      // Counterparty code for APX Part
  invoice_number?: string   // Invoice number for APX Part (mirage_invoice_number)
  invoice_date?: string     // Invoice date for APX Part (DD.MM.YYYY)
  level_acc?: number        // Account level: 0=parent, 1=normal, 2=needs APX
}

export interface ReconciliationLine {
  debit_account: string
  credit_account: string
  amount: string
  description?: string
}

export interface UpdateLinkAccountsRequest {
  debit_account?: string
  credit_account?: string
  debit_lines?: JournalLine[]
  credit_lines?: JournalLine[]
  reconciliation_lines?: ReconciliationLine[]
}

export interface MatchedPairPreview {
  debit_lines: JournalLine[]
  credit_lines: JournalLine[]
  document_number: string
  period: string
  available_debit_accounts: { code: string; name: string; type: string }[]
  available_bank_accounts: { code: string; name: string; type: string }[]
  suggested_debit_reasoning?: string
  suggested_debit_confidence?: string
  suggested_debit_alternatives?: string[]
  suggested_bank_reasoning?: string
  user_selected_debit_account?: string
  user_selected_credit_account?: string
  pending_selection?: boolean
  pending_message?: string
}

export interface MatchedPairData {
  link_id: string
  match_percentage: number
  match_success: string
  is_committed?: boolean
  committed_at?: string | null
  mirage_accrec_id?: number | null
  transaction: MatchedTransactionData
  invoice: MatchedInvoiceData
  preview: MatchedPairPreview
}

export interface MatchedPairsWithPreviewResponse {
  matched_pairs: MatchedPairData[]
  total: number
  skip: number
  limit: number
}

export interface BulkCommitRequest {
  link_ids: string[]
}

export interface TransactionDeleteRequest {
  transaction_ids: string[]
}

export interface TransactionDeleteResponse {
  deleted_count: number
  deleted_ids: string[]
  failed_ids: string[]
  errors: string[]
}

export interface CommitResult {
  link_id: string
  status: 'success' | 'error'
  mirage_accrec_id?: number
  error_message?: string
}

export interface BulkCommitResponse {
  message: string
  successful_commits: CommitResult[]
  failed_commits: CommitResult[]
  summary: {
    total_requested: number
    successful: number
    failed: number
  }
}

export interface UploadBankStatementResponse {
  message: string
  file_id?: string
  job_id?: string
  filename?: string
  status_url?: string
  processing_status?: string
  duplicate?: boolean
  existing_file?: {
    id: string
    original_filename: string
    uploaded_at: string | null
    client_id: string | null
    transaction_count?: number
    has_committed_links?: boolean
    committed_link_count?: number
  }
  checksum?: string
}

// Processed Files types
export interface ProcessedFileData {
  id: string
  original_filename: string
  mime_type: string
  size_bytes: number | null
  source: string
  ingested_at: string
  client_id: string | null
  client_name: string | null
  transaction_count: number
  status: 'processing' | 'completed' | 'failed'
  has_committed_links: boolean
  committed_link_count: number
}

export interface ProcessedFilesResponse {
  files: ProcessedFileData[]
  total: number
  skip: number
  limit: number
}

export interface FileDeleteCheckResponse {
  can_delete: boolean
  has_committed_links: boolean
  committed_link_count: number
  transaction_count: number
  warnings: string[]
}

export interface FileDeleteResponse {
  deleted: boolean
  file_id: string
  transactions_deleted: number
  warnings: string[]
}

export interface FileReprocessResponse {
  success: boolean
  message: string
  transactions_deleted: number
  warnings: string[]
}

export interface BankStatementProcessingStatus {
  file_id: string
  filename: string
  status: 'processing' | 'completed' | 'failed'
  message: string
  transactions_count: number
  uploaded_at: string
}

export interface TransactionFilters extends ReconciliationFilters {
  amount_min?: number
  amount_max?: number
}

export interface PartyInfo {
  name: string | null
  address: string | null
  tax_id: string | null
}

export interface InvoiceData {
  id: string
  invoice_number: string | null
  client_name: string | null
  client_id: string | null
  total_amount: number | null
  currency: string | null
  invoice_date: string | null
  status: string | null
  vendor: PartyInfo
  customer: PartyInfo
  // Add other invoice fields as needed
  links?: MatchResponse[]
}

export interface TransactionData {
  id: string
  source: string | null
  description: string | null
  reference: string | null
  amount: number | null
  booking_ts: string | null
  counterparty_name: string | null
  currency: string | null
  client_id: string | null
  client_name: string | null
  // Add other transaction fields as needed
  links?: MatchResponse[]
}

export interface MatchRequest {
  invoice_id: string
  transaction_id: string
  match_percentage?: number
  match_reason?: string
  notes?: string
  status?: string
}

export interface MatchUpdateRequest {
  match_percentage?: number
  match_reason?: string
  notes?: string
  status?: string
  lock_date?: string
}

export interface ApiResponse<T> {
  [key: string]: T | number
}

export interface InvoicesResponse {
  invoices: InvoiceData[]
  total: number
  company_name: string
}

export interface TransactionsResponse {
  transactions: TransactionData[]
  total: number
  company_name: string
}

export interface TransactionSummary {
  id: string
  amount: number
  currency: string
  booking_ts: string | null
  description: string | null
  reference: string | null
  counterparty_name: string | null
}

export interface InvoiceSummary {
  id: string
  client_id: string | null
  status: string | null
  filename?: string | null
  original_filename?: string | null
}

export interface MatchResponse {
  id: string
  invoice_id: string
  transaction_id: string
  match_percentage: number | null
  match_reason?: string
  notes?: string
  status: string
  is_automatic: boolean
  linked_at: string | null
  linked_by_user_id?: string | null
  lock_date?: string | null
  created_at?: string | null
  updated_at?: string | null
  transaction_summary?: TransactionSummary | null
  invoice_summary?: InvoiceSummary | null
  // Mirage commit tracking
  mirage_accrec_id?: number | null
  pushed_to_mirage_at?: string | null
  reversed_at?: string | null
}

export interface AutoMatchResponse {
  created: number
  attempted: number
  matches: MatchResponse[]
  skipped: {
    invoice_id: string
    transaction_id: string
    reason: string
  }[]
  warnings: string[]
}

export interface ClientData {
  id: string
  name: string
  status: string
  context?: string
  system_prompt?: string
  mssql_client_id?: string
  user_id: string
  created_at: string
  updated_at: string
  last_synced?: string
}

export interface ClientsResponse {
  clients: ClientData[]
  total: number
  limit: number
  offset: number
  query?: string
}

/**
 * Reconciliation API methods
 */
export const reconciliationApi = {
  /**
   * Get invoices for reconciliation
   */
  async getInvoices(filters: ReconciliationFilters = {}): Promise<InvoicesResponse> {
    const searchParams = new URLSearchParams()

    if (filters.match_status) searchParams.append('match_status', filters.match_status)
    if (filters.client_ids?.length) {
      filters.client_ids.forEach(id => searchParams.append('client_ids', id))
    }
    if (filters.search) searchParams.append('search', filters.search)
    if (filters.date_from) searchParams.append('date_from', filters.date_from)
    if (filters.date_to) searchParams.append('date_to', filters.date_to)
    if (filters.skip !== undefined) searchParams.append('skip', filters.skip.toString())
    if (filters.limit !== undefined) searchParams.append('limit', filters.limit.toString())

    const url = `/reconciliation/invoices${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
    return apiClient.get<InvoicesResponse>(url)
  },

  /**
   * Get transactions for reconciliation
   */
  async getTransactions(filters: TransactionFilters = {}): Promise<TransactionsResponse> {
    const searchParams = new URLSearchParams()

    if (filters.match_status) searchParams.append('match_status', filters.match_status)
    if (filters.client_ids?.length) {
      filters.client_ids.forEach(id => searchParams.append('client_ids', id))
    }
    if (filters.search) searchParams.append('search', filters.search)
    if (filters.date_from) searchParams.append('date_from', filters.date_from)
    if (filters.date_to) searchParams.append('date_to', filters.date_to)
    if (filters.amount_min !== undefined) searchParams.append('amount_min', filters.amount_min.toString())
    if (filters.amount_max !== undefined) searchParams.append('amount_max', filters.amount_max.toString())
    if (filters.skip !== undefined) searchParams.append('skip', filters.skip.toString())
    if (filters.limit !== undefined) searchParams.append('limit', filters.limit.toString())

    const url = `/reconciliation/transactions${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
    return apiClient.get<TransactionsResponse>(url)
  },

  /**
   * Create a new invoice-transaction match
   */
  async createMatch(matchData: MatchRequest): Promise<MatchResponse> {
    return apiClient.post<MatchResponse>('/reconciliation/reconcile', matchData)
  },

  /**
   * Unlink an existing match
   */
  async unlinkMatch(linkId: string): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`/reconciliation/unlink/${linkId}`)
  },

  /**
   * Update an existing match
   */
  async updateMatch(linkId: string, updateData: MatchUpdateRequest): Promise<MatchResponse> {
    return apiClient.put<MatchResponse>(`/reconciliation/link/${linkId}`, updateData)
  },

  async updateLinkAccounts(
    linkId: string,
    data: UpdateLinkAccountsRequest
  ): Promise<{ link_id: string; user_selected_debit_account: string | null; user_selected_credit_account: string | null }> {
    return apiClient.patch<{ link_id: string; user_selected_debit_account: string | null; user_selected_credit_account: string | null }>(
      `/reconciliation/link/${linkId}/accounts`,
      data
    )
  },

  /**
   * Trigger automatic matching using the current filters
   */
  async autoMatch(filters: TransactionFilters = {}): Promise<AutoMatchResponse> {
    return apiClient.post<AutoMatchResponse>('/reconciliation/auto-match', filters)
  },

  /**
   * Upload bank statement file
   */
  async uploadBankStatement(
    file: File,
    clientId?: string
  ): Promise<UploadBankStatementResponse> {
    const formData = new FormData()
    formData.append('file', file)
    if (clientId) {
      formData.append('client_id', clientId)
    }
    return apiClient.post<UploadBankStatementResponse>(
      '/reconciliation/upload',
      formData
    )
  },

  /**
   * Get bank statement processing status
   */
  async getBankStatementProcessingStatus(
    fileId: string
  ): Promise<BankStatementProcessingStatus> {
    if (!fileId || fileId === 'undefined' || fileId.trim() === '') {
      throw new Error('Invalid fileId provided to getBankStatementProcessingStatus')
    }

    return apiClient.get<BankStatementProcessingStatus>(
      `/reconciliation/upload/${fileId}/status`
    )
  },

  /**
   * Get matched pairs with preview data
   */
  async getMatchedPairsWithPreview(filters: ReconciliationFilters = {}): Promise<MatchedPairsWithPreviewResponse> {
    const searchParams = new URLSearchParams()

    if (filters.match_status) searchParams.append('match_status', filters.match_status)
    if (filters.client_ids?.length) {
      filters.client_ids.forEach(id => searchParams.append('client_ids', id))
    }
    if (filters.search) searchParams.append('search', filters.search)
    if (filters.skip !== undefined) searchParams.append('skip', filters.skip.toString())
    if (filters.limit !== undefined) searchParams.append('limit', filters.limit.toString())

    const url = `/reconciliation/matched-pairs-with-preview${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
    return apiClient.get<MatchedPairsWithPreviewResponse>(url)
  },

  /**
   * Unmatch a pair
   */
  async unmatchPair(linkId: string): Promise<{ message: string; link_id: string }> {
    return apiClient.delete<{ message: string; link_id: string }>(`/reconciliation/unmatch/${linkId}`)
  },

  /**
   * Bulk commit matched pairs to Mirage
   */
  async bulkCommit(request: BulkCommitRequest): Promise<BulkCommitResponse> {
    return apiClient.post<BulkCommitResponse>('/reconciliation/bulk-commit', request)
  },

  /**
   * Delete a single unmatched transaction
   */
  async deleteTransaction(transactionId: string): Promise<{ message: string; transaction_id: string }> {
    return apiClient.delete<{ message: string; transaction_id: string }>(
      `/reconciliation/transactions/${transactionId}`
    )
  },

  /**
   * Delete multiple unmatched transactions in bulk
   */
  async deleteTransactionsBulk(request: TransactionDeleteRequest): Promise<TransactionDeleteResponse> {
    return apiClient.delete<TransactionDeleteResponse>(
      '/reconciliation/transactions/bulk',
      { body: JSON.stringify(request) }
    )
  },

  /**
   * Get processed files with detailed information
   */
  async getProcessedFiles(
    clientId?: string,
    skip?: number,
    limit?: number
  ): Promise<ProcessedFilesResponse> {
    const searchParams = new URLSearchParams()

    if (clientId) searchParams.append('client_id', clientId)
    if (skip !== undefined) searchParams.append('skip', skip.toString())
    if (limit !== undefined) searchParams.append('limit', limit.toString())

    const url = `/reconciliation/files/detailed${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
    return apiClient.get<ProcessedFilesResponse>(url)
  },

  /**
   * Check if a file can be safely deleted
   */
  async checkFileDeleteSafety(fileId: string): Promise<FileDeleteCheckResponse> {
    return apiClient.get<FileDeleteCheckResponse>(`/reconciliation/files/${fileId}/delete-check`)
  },

  /**
   * Delete a transaction file and all its transactions
   */
  async deleteTransactionFile(fileId: string, force?: boolean): Promise<FileDeleteResponse> {
    const url = `/reconciliation/files/${fileId}${force ? '?force=true' : ''}`
    return apiClient.delete<FileDeleteResponse>(url)
  },

  /**
   * Delete an existing file to allow re-upload (reprocess)
   */
  async reprocessDuplicateFile(fileId: string, force?: boolean): Promise<FileReprocessResponse> {
    const url = `/reconciliation/files/${fileId}/reprocess${force ? '?force=true' : ''}`
    return apiClient.post<FileReprocessResponse>(url)
  }
}

// ===========================
// PROCESSING API METHODS
// ===========================

/**
 * Processing API methods for job status tracking
 */
export const processingApi = {
  /**
   * Get status of a specific processing job (for invoices)
   */
  async getJobStatus(jobId: string): Promise<{
    job_id: string;
    status: string;
    progress: number;
    current_step: string;
    error_message?: string;
  }> {
    return apiClient.get<{
      job_id: string;
      status: string;
      progress: number;
      current_step: string;
      error_message?: string;
    }>(`/processing/jobs/${jobId}/status`)
  },

  /**
   * Get status of a transaction processing job (for bank statements)
   */
  async getTransactionJobStatus(jobId: string): Promise<{
    job_id: string;
    status: string;
    progress: number;
    current_step: string;
    error_message?: string;
  }> {
    return apiClient.get<{
      job_id: string;
      status: string;
      progress: number;
      current_step: string;
      error_message?: string;
    }>(`/reconciliation/upload/jobs/${jobId}/status`)
  },

  /**
   * Get status of an uploaded bank statement file
   */
  async getFileStatus(fileId: string): Promise<{
    file_id: string;
    filename: string;
    status: 'processing' | 'completed' | 'failed';
    message: string;
    transactions_count: number;
    uploaded_at: string;
  }> {
    if (!fileId || fileId === 'undefined' || fileId.trim() === '') {
      throw new Error('Invalid fileId provided to getFileStatus')
    }

    return apiClient.get<{
      file_id: string;
      filename: string;
      status: 'processing' | 'completed' | 'failed';
      message: string;
      transactions_count: number;
      uploaded_at: string;
    }>(`/reconciliation/upload/${fileId}/status`)
  }
}

// ===========================
// CLIENTS API METHODS
// ===========================

/**
 * Clients API methods
 */
export const clientsApi = {
  /**
   * Get list of clients with optional search and pagination
   */
  async getClients(
    query?: string,
    limit: number = 1000,
    offset: number = 0,
    status?: string
  ): Promise<ClientsResponse> {
    const searchParams = new URLSearchParams()

    if (query) searchParams.append('q', query)
    searchParams.append('limit', limit.toString())
    searchParams.append('offset', offset.toString())
    if (status) searchParams.append('status', status)

    const url = `/clients?${searchParams.toString()}`
    return apiClient.get<ClientsResponse>(url)
  },

  /**
   * Get a single client by ID
   */
  async getClient(clientId: string): Promise<{ client: ClientData }> {
    return apiClient.get<{ client: ClientData }>(`/clients/${clientId}`)
  },

  /**
   * Create a new client
   */
  async createClient(clientData: {
    name: string
    status?: string
    context?: string
    system_prompt?: string
    mssql_client_id?: string
  }): Promise<{ message: string; client: ClientData }> {
    return apiClient.post<{ message: string; client: ClientData }>('/clients', clientData)
  },

  /**
   * Update a client
   */
  async updateClient(
    clientId: string,
    updateData: {
      context?: string
      system_prompt?: string
      status?: string
    }
  ): Promise<{ message: string; client: ClientData }> {
    return apiClient.patch<{ message: string; client: ClientData }>(`/clients/${clientId}`, updateData)
  },

  /**
   * Delete a client
   */
  async deleteClient(clientId: string): Promise<{ message: string }> {
    return apiClient.delete<{ message: string }>(`/clients/${clientId}`)
  }
}

// ===========================
// MIRAGE RECONCILIATION API METHODS
// ===========================

export interface ReconcileConfirmRequest {
  link_id: string
}

export interface ReconcileConfirmResponse {
  status: 'success' | 'error'
  commit_id?: string
  mirage_accrec_id?: number | null
  duration_seconds: number
  message: string
  error?: string | null
}

/**
 * Mirage Reconciliation API methods
 */
export const mirageReconcileApi = {
  /**
   * Confirm and execute reconciliation using persisted journal lines
   */
  async confirm(request: ReconcileConfirmRequest): Promise<ReconcileConfirmResponse> {
    return apiClient.post<ReconcileConfirmResponse>(
      '/mirage/reconcile/confirm',
      request
    )
  },

  /**
   * Get current reconciliation status for a link
   */
  // Additional reconciliation calls removed while backend endpoints are disabled.
}

// ===========================
// ERP REFERENCE DATA API METHODS
// ===========================

export interface AccAssignment {
  id: number
  description: string
  action_type?: number
  code?: string
  is_active: boolean
}

export interface Account {
  code: string
  name: string
  category: string
  level: number
}

export interface DealName {
  id: number
  vat_rate: number
  transaction_type: string
  description: string
  currency_code?: number
  valid_from?: string
  valid_to?: string
  vat_code?: string
}

export interface AccAssignmentsResponse {
  acc_assignments: AccAssignment[]
}

export interface AccountsResponse {
  accounts: Account[]
}

export interface DealNamesResponse {
  deal_names: DealName[]
}

export interface UnitOfMeasure {
  code: string
  name: string
  print?: string
}

export interface UnitsOfMeasureResponse {
  units_of_measure: UnitOfMeasure[]
}

/**
 * ERP Reference Data API methods
 */
export const erpApi = {
  /**
   * Get account assignments for a client
   */
  async getAccAssignments(
    clientId: string,
    actionType?: number
  ): Promise<AccAssignmentsResponse> {
    const searchParams = new URLSearchParams()

    if (actionType !== undefined) {
      searchParams.append('action_type', actionType.toString())
    }

    const url = `/clients/${clientId}/acc-assignment${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
    return apiClient.get<AccAssignmentsResponse>(url)
  },

  /**
   * Get chart of accounts
   */
  async getAccounts(): Promise<AccountsResponse> {
    return apiClient.get<AccountsResponse>('/accounts')
  },

  /**
   * Get deal names for a client
   */
  async getDealNames(
    clientId: string,
    options?: {
      vatRate?: number
      actionType?: number
    }
  ): Promise<DealNamesResponse> {
    const searchParams = new URLSearchParams()

    if (options?.vatRate !== undefined) {
      searchParams.append('vat_rate', options.vatRate.toString())
    }

    if (options?.actionType !== undefined) {
      searchParams.append('action_type', options.actionType.toString())
    }

    const url = `/clients/${clientId}/deal-names${searchParams.toString() ? `?${searchParams.toString()}` : ''}`
    return apiClient.get<DealNamesResponse>(url)
  },

  /**
   * Get units of measure for a client
   */
  async getUnitsOfMeasure(clientId: string): Promise<UnitsOfMeasureResponse> {
    return apiClient.get<UnitsOfMeasureResponse>(`/clients/${clientId}/units-of-measure`)
  }
}

// ===========================
// USERS API METHODS
// ===========================

export interface MirageUser {
  id: number
  username: string
  full_name: string
  is_active: boolean
}

export interface MirageUsersResponse {
  database_name: string
  users: MirageUser[]
}

export interface UserData {
  id: string
  email: string
  mirage_username: string
  mirage_user_id?: number | null
  full_name: string
  is_active: boolean
  is_superuser: boolean
  role: string
  status: string
  first_login_at?: string | null
  created_at: string
  updated_at: string
}

/**
 * Users API methods
 */
export const usersApi = {
  /**
   * Get Mirage users from the administrative Mirage database
   * Used for linking Saldo users to Mirage users for audit trail
   * Mirage users are shared across all clients
   */
  async getMirageUsers(): Promise<MirageUsersResponse> {
    return apiClient.get<MirageUsersResponse>('/users/mirage-users')
  },

  /**
   * Get current user information
   */
  async getCurrentUser(): Promise<UserData> {
    return apiClient.get<UserData>('/auth/me')
  },

  /**
   * Get user by ID
   */
  async getUser(userId: string): Promise<UserData> {
    return apiClient.get<UserData>(`/users/${userId}`)
  },

  /**
   * Update user (including Mirage user link)
   */
  async updateUser(
    userId: string,
    updateData: {
      mirage_user_id?: number | null
      mirage_username?: string | null
      full_name?: string
      email?: string
      is_active?: boolean
      role?: string
      status?: string
    }
  ): Promise<{ message: string; user: UserData }> {
    return apiClient.put<{ message: string; user: UserData }>(`/users/${userId}`, updateData)
  },

  /**
   * Update user role via the dedicated role endpoint
   */
  async updateUserRole(userId: string, role: string): Promise<UserData> {
    return apiClient.put<UserData>(`/admin/users/${userId}/role`, { role })
  }
}
